import React, { useId, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export interface AdminEditingCautionProps {
  contentLabel: string;
  ownerLabel: string;
  className?: string;
}

/**
 * Compact Super Admin cue for the floating owner toolbar.
 * Shows the full banner copy only on hover / keyboard focus — no layout shift.
 */
export const AdminEditingCaution: React.FC<AdminEditingCautionProps> = ({
  contentLabel,
  ownerLabel,
  className = '',
}) => {
  const tipId = useId();
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`relative inline-flex ${className}`.trim()}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="floating-toolbar-btn-muted !text-amber-300 hover:!text-amber-200 border-amber-500/35 hover:border-amber-400/50"
        aria-label={`Super Admin Mode: editing another user's ${contentLabel}`}
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <ExclamationTriangleIcon className="w-4 h-4" aria-hidden />
      </button>
      {open ? (
        <div
          id={tipId}
          role="tooltip"
          className="absolute bottom-full right-0 mb-2 w-[min(18rem,calc(100vw-2rem))] z-[80]
                     rounded-lg border border-yellow-600/50 bg-[#1a1508] p-3 shadow-xl shadow-black/50
                     text-left pointer-events-none"
        >
          <p className="text-yellow-300 font-medium text-sm leading-snug">
            Super Admin Mode: editing another user&apos;s {contentLabel}
          </p>
          <p className="text-yellow-200/80 text-xs mt-1 leading-relaxed">
            Owner: {ownerLabel}. Changes apply to their {contentLabel}.
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default AdminEditingCaution;
